import { Reveal } from "@/components/Reveal";
import { CheckCircle2 } from "lucide-react";
import { BRAND } from "@/constants/brand";

const IMG_TECH = "/assets/installer-main-v3.jpg";
const IMG_UNIT = "/assets/installer-secondary-v3.jpg";

const VALUES = [
  "Rigurosidad técnica en cada proyecto",
  "Máxima limpieza y cuidado en cada obra",
  "Puntualidad sin concesiones",
  "Asesoramiento térmico personalizado",
];

const FOUNDERS = [
  { initials: "CE", name: BRAND.founder, role: "Fundador" },
  { initials: "DO", name: BRAND.ceo, role: "CEO y Cofundador" },
];

export const About = () => (
  <section id="nosotros" data-testid="about-section" className="bg-white py-16 sm:py-24 lg:py-32">
    <div className="mx-auto grid max-w-7xl items-center gap-16 px-4 sm:px-6 lg:grid-cols-2 lg:px-12">
      <div className="relative">
        <Reveal>
          <div className="overflow-hidden rounded-[1.75rem] shadow-xl ring-1 ring-slate-200">
            <img
              src={IMG_TECH}
              alt="Dos instaladores de Clima Oliver con uniforme corporativo montando un split en un hogar"
              className="aspect-[4/3] w-full object-cover"
              data-testid="about-image-main"
            />
          </div>
        </Reveal>
        <Reveal delay={0.15} className="absolute -bottom-10 -right-2 hidden w-56 sm:block lg:-right-8">
          <div className="overflow-hidden rounded-2xl border-4 border-white shadow-2xl">
            <img
              src={IMG_UNIT}
              alt="Dos trabajadores de Clima Oliver terminando la instalación de un split residencial"
              className="aspect-square w-full object-cover"
              data-testid="about-image-secondary"
            />
          </div>
        </Reveal>
        <div className="absolute -left-3 top-8 rounded-2xl bg-navy px-5 py-4 text-white shadow-xl lg:-left-8">
          <p className="font-display text-3xl font-extrabold text-ocean-500">2021</p>
          <p className="text-xs uppercase tracking-widest text-slate-300">Año de fundación</p>
        </div>
      </div>

      <div>
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-ocean-600">
            Sobre Nosotros
          </p>
          <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl lg:text-4xl text-balance">
            Nuestra historia y compromiso institucional
          </h2>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-6 space-y-5 text-base leading-relaxed text-slate-600">
            <p>
              Clima Oliver nace en el año 2021 de la visión de su fundador,
              <strong className="text-navy"> Cleverson Elvis</strong>, junto a su CEO y
              cofundador, <strong className="text-navy">David de Oliveira</strong>, con un
              propósito inequívoco: elevar el estándar de las instalaciones de aire
              acondicionado en España mediante un servicio formal, transparente y
              técnicamente impecable.
            </p>
            <p>
              A lo largo de estos 5 años de trayectoria, hemos consolidado un método de
              trabajo que combina el estudio térmico detallado de cada estancia, la
              selección rigurosa de equipos de alta eficiencia y una ejecución cuidada
              hasta el último detalle: tuberías perfectamente aisladas, desagües
              verificados, acabados limpios y pruebas de estanqueidad exhaustivas antes
              de dar por concluida cualquier intervención.
            </p>
            <p>
              Entendemos que instalar climatización no es únicamente montar un equipo:
              es asumir un compromiso de confort y confianza con cada cliente. Por ello,
              todas nuestras instalaciones incluyen <strong className="text-navy">2 años
              de garantía sobre la instalación</strong> y <strong className="text-navy">
              3 años de garantía sobre la máquina</strong>, documentados por escrito y
              respaldados por un servicio posventa que responde con la misma seriedad
              con la que instalamos.
            </p>
            <p>
              Prestamos servicio en toda la provincia de <strong className="text-navy">Tarragona
              y Barcelona</strong>, desplazándonos a domicilios y negocios de toda la zona con
              la misma puntualidad y el mismo estándar de calidad que nos ha hecho crecer
              desde el primer día.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.18}>
          <ul className="mt-7 grid gap-3 sm:grid-cols-2" data-testid="about-values-list">
            {VALUES.map((v) => (
              <li key={v} className="flex items-start gap-2.5 text-sm font-medium text-slate-700">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-ocean-600" />
                {v}
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal delay={0.24}>
          <div className="mt-9 grid gap-4 sm:grid-cols-2">
            {FOUNDERS.map((f) => (
              <div
                key={f.name}
                data-testid={`founder-card-${f.initials.toLowerCase()}`}
                className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-slate-50/60 p-4 transition-colors duration-300 hover:border-ocean-600/30"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-navy font-display text-sm font-bold text-white">
                  {f.initials}
                </span>
                <div>
                  <p className="font-display text-sm font-bold text-navy">{f.name}</p>
                  <p className="text-xs text-slate-500">{f.role}</p>
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
